import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Database, Globe, Search, X } from 'lucide-react';
import { BrandMark } from '../layout/Brand';
import { Kbd } from '../ui/misc';
import { useSources } from '../../context/SourcesContext';
import { useAuth } from '../../context/AuthContext';
import {
  addRecentSearch, clearRecentSearches, removeRecentSearch, useRecentSearches,
} from '../../hooks/useRecentSearches';
import { newSearchParams } from '../../lib/searchParams';
import { formatDate } from '../../lib/format';

const EXAMPLES = ['Ferritin', 'HbA1c', 'Vitamin D', 'Lipase', 'TSH', 'Coeliac serology', 'Troponin', 'Lead'];

const Chip = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-surface px-3 py-1.5 text-sm text-fg-2 transition-colors hover:border-accent/50 hover:text-fg"
  >
    {children}
  </button>
);

/** Shown before any search: examples, recent searches and data freshness. */
export const StartPage = () => {
  const navigate = useNavigate();
  const recent = useRecentSearches();
  const { labs, customFolders, status, scope, effectiveMode } = useSources();
  const { isAdmin } = useAuth();

  const run = (q) => {
    addRecentSearch(q);
    const params = newSearchParams(q, { mode: effectiveMode(scope.mode), match: scope.match });
    navigate(`/search?${params.toString()}`, { state: { nonce: Date.now() } });
  };

  const freshness = useMemo(() => {
    const cached = labs.filter((l) => l.hasLocalData);
    const stamps = cached.map((l) => l.dataTimestamp).filter(Boolean);
    return {
      cachedCount: cached.length,
      liveCount: labs.length - cached.length,
      oldest: stamps.length ? Math.min(...stamps) : 0,
      newest: stamps.length ? Math.max(...stamps) : 0,
      records: cached.reduce((n, l) => n + l.recordCount, 0),
    };
  }, [labs]);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-16 pt-10 sm:pt-16">
      <div className="text-center">
        <div className="flex justify-center"><BrandMark size="lg" /></div>
        <h1 className="mt-5 text-2xl font-semibold tracking-tight text-fg sm:text-3xl">
          Find collection requirements across Australian labs
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base text-muted">
          Search {status === 'ready' ? `${labs.length + customFolders.length} lab directories` : 'lab directories'} at once
          by test name, synonym or code, then compare specimen, tube and stability side by side.
        </p>
        <p className="mt-3 hidden text-sm text-muted sm:block">
          Press <Kbd>/</Kbd> to start typing.
        </p>
      </div>

      <section aria-labelledby="examples-heading" className="mt-10">
        <h2 id="examples-heading" className="mb-3 text-xs font-semibold uppercase tracking-wider text-faint">Try</h2>
        <div className="flex flex-wrap gap-2">
          {EXAMPLES.map((q) => (
            <Chip key={q} onClick={() => run(q)}>
              <Search size={13} className="text-faint" aria-hidden="true" /> {q}
            </Chip>
          ))}
        </div>
      </section>

      {recent.length > 0 && (
        <section aria-labelledby="recent-heading" className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 id="recent-heading" className="text-xs font-semibold uppercase tracking-wider text-faint">Recent searches</h2>
            <button type="button" onClick={clearRecentSearches} className="text-xs text-muted hover:text-fg">
              Clear
            </button>
          </div>
          <ul className="flex flex-wrap gap-2">
            {recent.map((q) => (
              <li key={q} className="inline-flex items-center rounded-full border border-line-strong bg-surface text-sm">
                <button type="button" onClick={() => run(q)} className="inline-flex items-center gap-1.5 py-1.5 pl-3 pr-1.5 text-fg-2 hover:text-fg">
                  <Clock size={13} className="text-faint" aria-hidden="true" /> {q}
                </button>
                <button
                  type="button"
                  onClick={() => removeRecentSearch(q)}
                  className="mr-1 rounded-full p-1 text-faint hover:bg-raised hover:text-fg"
                  aria-label={`Remove "${q}" from recent searches`}
                >
                  <X size={12} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {status === 'ready' && (
        <section aria-labelledby="data-heading" className="mt-10 rounded-2xl border border-line bg-surface p-5">
          <h2 id="data-heading" className="text-sm font-semibold text-fg">About the data</h2>
          <ul className="mt-3 space-y-2.5 text-sm text-fg-2">
            <li className="flex gap-3">
              <Database size={16} className="mt-0.5 shrink-0 text-accent-fg" aria-hidden="true" />
              <span>
                <strong className="font-medium text-fg">{freshness.cachedCount} labs</strong> are searched instantly from
                cached copies of their directories ({freshness.records.toLocaleString()} tests), last refreshed
                between {formatDate(freshness.oldest)} and {formatDate(freshness.newest)}.
              </span>
            </li>
            <li className="flex gap-3">
              <Globe size={16} className="mt-0.5 shrink-0 text-info" aria-hidden="true" />
              <span>
                <strong className="font-medium text-fg">{freshness.liveCount} labs</strong> have no cached copy and are
                checked on their live websites, which takes a few seconds longer.
              </span>
            </li>
          </ul>
          <p className="mt-4 text-sm text-muted">
            Always confirm requirements on the lab&apos;s own website before collecting.{' '}
            <Link to="/resources" className="text-accent-fg underline underline-offset-4">Lab handbooks &amp; resources</Link>
            {isAdmin && (
              <>
                {' · '}
                <Link to="/admin/sources" className="text-accent-fg underline underline-offset-4">Manage data sources</Link>
              </>
            )}
          </p>
        </section>
      )}
    </div>
  );
};
